import type { ProgramContentSlideOwnerRef } from '../types/programContentSlideCanvas';
import { isPersistableUuid } from '../utils/programContentSlideOwner';
import {
  readProgramPresentationListStatsCache,
  writeProgramPresentationListStatsCache,
  type SlideOwnerStats,
} from '../utils/programPresentationListStatsCache';
import { programContentSlidePresenceKey } from './programContentSlideService';
import { supabase } from '../supabaseClient';

export type { SlideOwnerStats };

function chunkIds<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

function layoutJsonByteSize(raw: unknown): number {
  try {
    return new Blob([JSON.stringify(raw ?? {})]).size;
  } catch {
    return String(raw ?? '').length;
  }
}

function upsertStats(
  map: Map<string, SlideOwnerStats>,
  kind: ProgramContentSlideOwnerRef['kind'],
  id: string,
  layoutJson: unknown
): void {
  const oid = id.trim();
  if (!oid) return;
  const key = programContentSlidePresenceKey({ kind, id: oid });
  const prev = map.get(key) ?? { pageCount: 0, layoutBytes: 0 };
  prev.pageCount += 1;
  prev.layoutBytes += layoutJsonByteSize(layoutJson);
  map.set(key, prev);
}

function collectUniqueOwners(owners: ProgramContentSlideOwnerRef[]): {
  keys: string[];
  cells: string[];
  scoped: string[];
  fixed: string[];
  refs: string[];
} {
  const seen = new Set<string>();
  const keys: string[] = [];
  const cells: string[] = [];
  const scoped: string[] = [];
  const fixed: string[] = [];
  const refs: string[] = [];
  owners.forEach((o) => {
    if (!o?.id?.trim() || !isPersistableUuid(o.id)) return;
    const k = programContentSlidePresenceKey(o);
    if (seen.has(k)) return;
    seen.add(k);
    keys.push(k);
    if (o.kind === 'program_cell') cells.push(o.id);
    else if (o.kind === 'scoped_row') scoped.push(o.id);
    else if (o.kind === 'fixed_row') fixed.push(o.id);
    else if (o.kind === 'presentation_ref') refs.push(o.id);
  });
  return { keys, cells, scoped, fixed, refs };
}

/** owner별 저장된 슬라이드 페이지 수·layout_json 용량(추정). LS 캐시로 재조회 최소화 */
export async function fetchSlideOwnerStatsMap(
  workspaceId: string,
  owners: ProgramContentSlideOwnerRef[],
  options?: { forceRefresh?: boolean; signal?: AbortSignal }
): Promise<Map<string, SlideOwnerStats>> {
  const sid = workspaceId.trim();
  const out = new Map<string, SlideOwnerStats>();
  if (!sid || !owners.length) return out;

  const { keys, cells, scoped, fixed, refs } = collectUniqueOwners(owners);
  if (!keys.length) return out;

  if (!options?.forceRefresh) {
    const cached = readProgramPresentationListStatsCache(sid, keys);
    cached.forEach((stats, key) => out.set(key, stats));
  }

  const missingKeys = keys.filter((k) => !out.has(k));
  if (!missingKeys.length) return out;

  const missingSet = new Set(missingKeys);
  const missingOwners = owners.filter((o) => {
    if (!o?.id?.trim() || !isPersistableUuid(o.id)) return false;
    return missingSet.has(programContentSlidePresenceKey(o));
  });
  const {
    cells: missCells,
    scoped: missScoped,
    fixed: missFixed,
    refs: missRefs,
  } = collectUniqueOwners(missingOwners);

  const fetched = new Map<string, SlideOwnerStats>();

  const loadColumn = async (
    column: 'program_progress_cell_id' | 'program_scoped_content_row_id' | 'program_fixed_content_row_id' | 'presentation_ref_id',
    ids: string[],
    kind: ProgramContentSlideOwnerRef['kind']
  ) => {
    for (const batch of chunkIds(ids, 40)) {
      if (!batch.length) continue;
      const { data, error } = await supabase
        .from('program_content_slide_pages')
        .select(`${column}, page_index, layout_json`)
        .eq('workspace_id', sid)
        .in(column, batch);
      if (error) throw new Error(error.message);
      (data ?? []).forEach((row) => {
        const id = String((row as Record<string, unknown>)[column] ?? '').trim();
        if (!id) return;
        upsertStats(fetched, kind, id, (row as Record<string, unknown>).layout_json);
      });
    }
  };

  await Promise.all([
    loadColumn('program_progress_cell_id', missCells, 'program_cell'),
    loadColumn('program_scoped_content_row_id', missScoped, 'scoped_row'),
    loadColumn('program_fixed_content_row_id', missFixed, 'fixed_row'),
    loadColumn('presentation_ref_id', missRefs, 'presentation_ref'),
  ]);

  missingKeys.forEach((key) => {
    const stats = fetched.get(key) ?? { pageCount: 0, layoutBytes: 0 };
    out.set(key, stats);
  });
  if (!options?.signal?.aborted) {
    const patch = new Map<string, SlideOwnerStats>();
    missingKeys.forEach((key) => {
      patch.set(key, out.get(key)!);
    });
    writeProgramPresentationListStatsCache(sid, patch);
  }

  return out;
}
